import { dedent } from 'ts-dedent';
import { MILES_TO_M } from '../const';
import { ColumnDataType, Provider, WorkflowCodeType, WorkflowComponentParamType } from '../types';
import { addColumns } from '../utils/schemas';
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent';
class DistanceToNearest extends SingleOutputWorkflowComponent {
    constructor() {
        super({
            name: 'native.distance',
            group: 'Spatial Operations',
            title: 'Distance to nearest',
            description: `
      This component calculates the minimum distance between geographies of an input table and those of a second table.
      `,
            starred: true
        }, [
            {
                name: 'main',
                type: WorkflowComponentParamType.InputTable,
                title: 'Main table'
            },
            {
                name: 'secondary',
                type: WorkflowComponentParamType.InputTable,
                title: 'Secondary table'
            },
            {
                name: 'geomain',
                type: WorkflowComponentParamType.Column,
                title: 'Geo column in main table',
                parent: 'main',
                dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
            },
            {
                name: 'geosecondary',
                type: WorkflowComponentParamType.Column,
                title: 'Geo column in secondary table',
                parent: 'secondary',
                dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
            },
            {
                name: 'idmain',
                type: WorkflowComponentParamType.Column,
                title: 'Id column in main table',
                parent: 'main'
            },
            {
                name: 'idsecondary',
                type: WorkflowComponentParamType.Column,
                title: 'Id column in secondary table',
                parent: 'secondary'
            },
            {
                name: 'radius',
                type: WorkflowComponentParamType.Number,
                title: 'Radius',
                min: 0
            },
            {
                name: 'units',
                type: WorkflowComponentParamType.Selection,
                title: 'Units',
                options: ['Meters', 'Miles'],
                default: 'Meters'
            }
        ]);
        this.supportedProviders = [
            Provider.BigQuery,
            Provider.Snowflake,
            Provider.Redshift,
            Provider.Postgres
        ];
    }
    async getCode({ workflow, params, connection, codeType, schemas }) {
        const dryRun = codeType === WorkflowCodeType.DryRun;
        let outputTablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'result', codeType);
        const preparedParams = this._prepareParameters(params);
        const convert = params.units === 'Meters' ? '' : `/ ${MILES_TO_M}`;
        const convert_to_m = params.units === 'Meters' ? '' : `* ${MILES_TO_M}`;
        let query = '';
        switch (connection.type) {
            case Provider.BigQuery: {
                const select = `WITH nearest AS (
                          SELECT a.${preparedParams.idmain} AS main_id,
                              ARRAY_AGG(
                                  STRUCT(
                                      b.${preparedParams.idsecondary} AS nearest_id,
                                      ST_DISTANCE(
                                          a.${preparedParams.geomain},
                                          b.${preparedParams.geosecondary}
                                      ) AS nearest_distance
                                  )
                                  ORDER BY ST_DISTANCE(
                                          a.${preparedParams.geomain},
                                          b.${preparedParams.geosecondary}
                                      )
                                  LIMIT 1
                              ) [ORDINAL(1)] AS nearest
                          FROM \`${preparedParams.main}\` a
                              JOIN \`${preparedParams.secondary}\` b
                                ON ST_DWITHIN(
                                  a.${preparedParams.geomain},
                                  b.${preparedParams.geosecondary},
                                  ${preparedParams.radius} ${convert_to_m}
                                )
                          GROUP BY a.${preparedParams.idmain}
                        )
                        SELECT a.*,
                            n.nearest.nearest_id AS nearest_id,
                            n.nearest.nearest_distance ${convert} AS nearest_distance
                        FROM nearest n
                            JOIN \`${preparedParams.main}\` a
                              ON a.${preparedParams.idmain} = n.main_id`;
                query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null);
                break;
            }
            case Provider.Postgres: {
                if (dryRun) {
                    const select = `SELECT a.*,
              0::DOUBLE PRECISION AS nearest_distance,
              b.${preparedParams.idsecondary} AS nearest_id
              FROM ${preparedParams.main} a, ${preparedParams.secondary} b
              LIMIT 0
            `;
                    query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null);
                }
                else {
                    const select = `SELECT %s,
                            _nearest_distance ${convert} AS nearest_distance,
                            _nearest_id AS nearest_id
                          FROM (
                            SELECT a.*,
                                b.${preparedParams.idsecondary} AS _nearest_id,
                                ST_DISTANCE(
                                    a.${preparedParams.geomain}::geography,
                                    b.${preparedParams.geosecondary}::geography
                                ) AS _nearest_distance,
                                RANK() OVER (
                                    partition by a.${preparedParams.idmain}
                                    ORDER BY ST_DISTANCE(
                                            a.${preparedParams.geomain}::geography,
                                            b.${preparedParams.geosecondary}::geography
                                        ) ASC
                                ) AS _distance_rank
                            FROM ${preparedParams.main} a
                                JOIN ${preparedParams.secondary} b ON
                                  ST_DWITHIN(
                                    a.${preparedParams.geomain}::geography,
                                    b.${preparedParams.geosecondary}::geography,
                                    ${preparedParams.radius} ${convert_to_m}
                                  )
                          ) t
                          WHERE _distance_rank = 1;`;
                    const createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null);
                    const tokens = (preparedParams.main ?? '').split('.');
                    const datasetname = tokens[0];
                    const schemaname = tokens[1];
                    const tablename = tokens[2];
                    query = dedent `DECLARE
                            alias TEXT;
                          BEGIN
                            EXECUTE '
                              with __alias AS(
                                SELECT column_name AS col_alias
                                FROM "${datasetname}".INFORMATION_SCHEMA.COLUMNS
                                WHERE table_name ilike ''${tablename}'' and
                                  table_schema ilike ''${schemaname}'' and
                                  table_catalog ilike ''${datasetname}''
                              )
                              SELECT STRING_AGG(col_alias, '', '') aliases
                              FROM __alias
                            '
                            INTO alias;
                            EXECUTE
                              REPLACE(
                                '${createTable}',
                                '%s',
                                alias
                              );
                          END;`;
                }
                break;
            }
            case Provider.Snowflake: {
                const select = `SELECT
                            * EXCLUDE (_distance_rank, _nearest_distance, _nearest_id),
                            _nearest_distance ${convert} AS nearest_distance,
                            _nearest_id AS nearest_id
                        FROM (
                                SELECT
                                    a.*,
                                    b.${preparedParams.idsecondary} AS _nearest_id,
                                    ST_DISTANCE(
                                        TO_GEOGRAPHY( a.${preparedParams.geomain} ),
                                        TO_GEOGRAPHY( b.${preparedParams.geosecondary} )
                                    ) AS _nearest_distance ,
                                    RANK() OVER (
                                        partition by a.${preparedParams.idmain}
                                        ORDER BY ST_DISTANCE(
                                            TO_GEOGRAPHY( a.${preparedParams.geomain} ),
                                            TO_GEOGRAPHY( b.${preparedParams.geosecondary} )
                                        ) ASC
                                    ) AS _distance_rank
                                FROM ${preparedParams.main} a
                                    JOIN ${preparedParams.secondary} b ON
                                      ST_DWITHIN(
                                        TO_GEOGRAPHY( a.${preparedParams.geomain} ),
                                        TO_GEOGRAPHY( b.${preparedParams.geosecondary} ),
                                        ${preparedParams.radius} ${convert_to_m}
                                      )
                              )
                        WHERE _distance_rank = 1`;
                query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null);
                break;
            }
            case Provider.Redshift: {
                const select = `SELECT %s,
                            _nearest_distance ${convert} AS nearest_distance,
                            _nearest_id AS nearest_id
                        FROM (
                                SELECT a.*,
                                    b.${preparedParams.idsecondary} AS _nearest_id,
                                    ST_DISTANCE(
                                        a.${preparedParams.geomain}::geography,
                                        b.${preparedParams.geosecondary}::geography
                                    ) AS _nearest_distance,
                                    RANK() OVER (
                                        partition by a.${preparedParams.idmain}
                                        ORDER BY _nearest_distance ASC
                                    ) AS _distance_rank
                                FROM ${preparedParams.main} a
                                    JOIN ${preparedParams.secondary} b ON
                                      ST_DWITHIN(
                                        -- need to transform because ST_TRANSFORM
                                        -- in RS do not accept geography
                                        ST_TRANSFORM(a.${preparedParams.geomain}, 3857),
                                        ST_TRANSFORM(b.${preparedParams.geosecondary}, 3857),
                                        ${preparedParams.radius} ${convert_to_m}
                                      )
                              )
                        WHERE _distance_rank = 1`;
                const tempselect = connection.getTempSelectQuery(select, codeType === WorkflowCodeType.DryRun);
                const tokens = (preparedParams.main ?? '').split('.');
                const datasetname = tokens[0];
                const schemaname = tokens[1];
                const tablename = tokens[2];
                const temptable = `${outputTablename}_temp`;
                const subquery = dedent `
                DROP TABLE IF EXISTS ${temptable};
                EXECUTE '
                  CREATE TABLE ${temptable} AS
                    SELECT DISTINCT
                      column_name
                    AS col_alias
                    FROM svv_columns
                    WHERE table_name ILIKE ''${tablename}'' AND
                      table_schema ILIKE ''${schemaname}'' AND
                      table_catalog ILIKE ''${datasetname}''
                ';
                EXECUTE '
                  SELECT LISTAGG(col_alias, '', '') aliases
                  FROM ${temptable};
                ' INTO _alias;
                DROP TABLE IF EXISTS ${outputTablename};
                EXECUTE
                  REPLACE(
                    'CREATE TABLE ${outputTablename} AS
                    ${tempselect}',
                    '%s',
                    _alias
                  );
                DROP TABLE ${temptable};
              `;
                query = dedent `
                    DECLARE
                      _alias VARCHAR(MAX);
                    BEGIN
                      ${connection.wrapCodeInIfTableExistsBlock(dedent(subquery), [outputTablename], codeType)}
                    END;`;
                break;
            }
            default:
                return null;
        }
        return {
            query,
            outputs: { result: outputTablename }
        };
    }
    canProvideOutputSchemas() {
        return true;
    }
    getOutputSchema({ columns, params }) {
        return addColumns(columns.main, [
            {
                name: `nearest_id`,
                type: columns.secondary.find((c) => c.name === params.idsecondary)
                    ?.type
            },
            {
                name: `nearest_distance`,
                type: ColumnDataType.Number
            }
        ]);
    }
}
export { DistanceToNearest };
